/**
 * SmartRouterAssistant — the conversational "ask anything" surface for Smart
 * Start. The user types a request; the backend (/api/smart-router/ask) either
 * answers it (grounded in the app's module knowledge + project-document RAG) or
 * suggests/auto-routes to the right module.
 *
 * Behavior:
 *  - High-confidence pure route (auto_route) → caller is asked to navigate.
 *  - Otherwise → render the answer + "Open <Module>" chips the user can click.
 *  - Ambiguous → a clarifying question is shown; the user can just keep typing.
 *  - "Set up a guided workflow instead" hands off to the existing pipeline flow.
 *
 * Multi-turn: the running conversation is sent back on every turn so follow-ups
 * keep context. Streaming is added in a later phase; this is request/response.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Send, ArrowRight, Sparkles, MessageSquare, FileText, Workflow } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import toast from 'react-hot-toast';
import {
  askSmartRouter,
  type SmartRouterAnswer,
  type SmartRouterModuleSuggestion,
  type SmartRouterTurn,
} from '../../../services/smartStartApi';

interface AssistantTurn {
  role: 'user' | 'assistant';
  content: string;
  result?: SmartRouterAnswer;
}

interface SmartRouterAssistantProps {
  initialMessage: string;
  projectId: number | null;
  /** Hand off to the existing guided pipeline flow with the given goal text. */
  onUseWorkflow: (goal: string) => void;
  onBack: () => void;
}

const SmartRouterAssistant: React.FC<SmartRouterAssistantProps> = ({
  initialMessage,
  projectId,
  onUseWorkflow,
  onBack,
}) => {
  const navigate = useNavigate();
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const firedInitial = useRef(false);

  const scrollToEnd = () => {
    setTimeout(() => scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' }), 50);
  };

  const send = async (message: string) => {
    const text = message.trim();
    if (!text || loading) return;

    const priorConversation: SmartRouterTurn[] = turns.map((t) => ({ role: t.role, content: t.content }));
    setTurns((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    scrollToEnd();

    try {
      const result = await askSmartRouter(text, priorConversation, projectId);

      // High-confidence pure route → navigate straight there.
      if (result.auto_route && result.route) {
        setTurns((prev) => [
          ...prev,
          { role: 'assistant', content: result.answer || `Taking you to ${result.route!.name}…`, result },
        ]);
        toast.success(`Opening ${result.route.name}`);
        setTimeout(() => navigate(result.route!.target_route), 600);
        return;
      }

      setTurns((prev) => [
        ...prev,
        { role: 'assistant', content: result.answer || result.clarifying_question || '', result },
      ]);
    } catch (err: any) {
      setTurns((prev) => [
        ...prev,
        { role: 'assistant', content: `Sorry — ${err?.response?.data?.error || err.message || 'something went wrong'}.` },
      ]);
    } finally {
      setLoading(false);
      scrollToEnd();
    }
  };

  useEffect(() => {
    if (firedInitial.current) return;
    firedInitial.current = true;
    if (initialMessage && initialMessage.trim()) send(initialMessage);
  }, []); // eslint-disable-line

  const openModule = (m: SmartRouterModuleSuggestion) => {
    navigate(m.target_route);
  };

  const renderChips = (result: SmartRouterAnswer) => {
    const chips: SmartRouterModuleSuggestion[] = [];
    if (result.route) chips.push(result.route);
    for (const s of result.suggested_modules || []) {
      if (!chips.find((c) => c.module === s.module)) chips.push(s);
    }
    if (chips.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-3">
        {chips.map((m) => (
          <button
            key={m.module}
            onClick={() => openModule(m)}
            className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-lavender/10 text-lavender border border-lavender/30 hover:bg-lavender hover:text-white transition-colors"
            title={m.reason || `Open ${m.name}`}
          >
            <ArrowRight className="w-3.5 h-3.5" />
            Open {m.name}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-gray-700">
          <Sparkles className="w-5 h-5 text-lavender" />
          <span className="text-sm font-semibold">Smart Router</span>
        </div>
        <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-700">Start over</button>
      </div>

      <div
        ref={scrollerRef}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 max-h-[60vh] overflow-y-auto"
      >
        {turns.length === 0 && !loading && (
          <div className="text-center py-10 text-gray-400">
            <MessageSquare className="w-7 h-7 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Ask anything about your project or the app.</p>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className={`flex mb-4 ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {t.role === 'user' ? (
              <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-lavender text-white">
                {t.content}
              </div>
            ) : (
              <div className="w-full rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm">
                {t.content && (
                  <div className="prose prose-sm max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{t.content}</ReactMarkdown>
                  </div>
                )}
                {t.result?.used_document && t.result.citations && t.result.citations.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-gray-400">
                    <FileText className="w-3.5 h-3.5" />
                    <span>Sources:</span>
                    {t.result.citations.map((c, ci) => (
                      <span key={ci} className="px-1.5 py-0.5 bg-white border border-gray-200 rounded">
                        {c.name || `Document ${c.document_id}`}
                      </span>
                    ))}
                  </div>
                )}
                {t.result && renderChips(t.result)}
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start mb-2">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
              <span className="text-xs text-gray-400">Thinking…</span>
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(input); }}
        className="mt-4 bg-white rounded-2xl border border-gray-200 shadow-sm focus-within:border-lavender"
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); } }}
          placeholder="Ask a follow-up, or describe what you want to do…"
          rows={2}
          disabled={loading}
          className="w-full px-4 py-3 text-sm bg-transparent resize-none focus:outline-none disabled:opacity-50"
        />
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100">
          <button
            type="button"
            onClick={() => onUseWorkflow(initialMessage || input || '')}
            className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-lavender"
            title="Set up a guided, step-by-step workflow instead"
          >
            <Workflow className="w-3.5 h-3.5" />
            Set up a guided workflow instead
          </button>
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-lavender text-white rounded-lg text-sm font-semibold disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

export default SmartRouterAssistant;
